import { ArrowDownLeft, ArrowUpRight, Loader2, RefreshCw, Printer, FileSpreadsheet } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { toast } from 'sonner'

import {
  cashHistoryService,
  type CashDenomination,
  type CashMovementHistory,
  type CashSessionDetailResponse,
  type CashSessionStatus,
} from '../../services/cash-history.service'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { GlobalPaginator } from '../ui/global-paginator'

const detailStatus: Record<CashSessionStatus, { label: string; className: string }> = {
  OPEN: { label: 'Abierta', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  CLOSED: { label: 'Cerrada', className: 'border-slate-200 bg-slate-100 text-slate-700' },
  CANCELLED: { label: 'Cancelada', className: 'border-red-200 bg-red-50 text-red-700' },
}

const movementNames: Record<string, string> = {
  OPENING: 'Apertura',
  SALE: 'Venta',
  RETURN: 'Devolución',
  WITHDRAWAL: 'Retiro',
  DEPOSIT: 'Depósito',
  CLOSING_ADJUSTMENT: 'Ajuste de cierre',
}

function money(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount)
}

function dateTime(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function person(user: { name: string; last_name?: string | null } | null): string {
  return user ? [user.name, user.last_name].filter(Boolean).join(' ') : '—'
}

function DenominationTable({ title, rows }: { title: string; rows: CashDenomination[] }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-sm font-semibold text-table-foreground">{title}</h3>
      <div className="overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Denominación</th><th className="px-3 py-2 text-right">Cantidad</th><th className="px-3 py-2 text-right">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-table-foreground/70">Sin desglose registrado</td></tr>
            ) : rows.map((row) => (
              <tr key={`${title}-${row.denomination}`}>
                <td className="px-3 py-2 text-table-foreground">
  {money(row.denomination)}
</td>

<td className="px-3 py-2 text-right text-table-foreground">
  {row.quantity}
</td>

<td className="px-3 py-2 text-right font-medium text-table-foreground">
  {money(row.total_amount)}
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function CashSessionDetailModal({
  sessionId,
  open,
  onOpenChange,
  autoPrint = false,
  onPrintComplete,
}: {
  sessionId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  autoPrint?: boolean
  onPrintComplete?: () => void
}) {
  const [detail, setDetail] = useState<CashSessionDetailResponse | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const ticketRef = useRef<HTMLDivElement>(null)
  const [ticketCopies, setTicketCopies] = useState(1)

  const printTicket = () => {
    const ticketEl = ticketRef.current
    if (!ticketEl) return

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.top = '-9999px'
    iframe.style.left = '-9999px'
    iframe.style.width = '80mm'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    if (!doc) {
      document.body.removeChild(iframe)
      return
    }

    doc.open()
    const copiesHTML = Array.from({ length: ticketCopies || 1 })
      .map(() => `<div>${ticketEl.innerHTML}</div>`)
      .join('<div style="page-break-after: always;"></div>')

    doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Corte de Caja</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: 80mm auto; margin: 4mm 3mm; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 11px; color: #000; width: 74mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .text-center  { text-align: center; }
    .font-bold    { font-weight: 700; }
    .text-xs      { font-size: 10px; }
    .text-sm      { font-size: 11px; }
    .text-base    { font-size: 13px; }
    .mb-2         { margin-bottom: 4px; }
    .mb-3         { margin-bottom: 6px; }
    .mb-4         { margin-bottom: 8px; }
    .mt-1         { margin-top: 2px; }
    .mt-2         { margin-top: 4px; }
    .mt-3         { margin-top: 6px; }
    .mt-4         { margin-top: 8px; }
    .mt-6         { margin-top: 12px; }
    .pt-1         { padding-top: 2px; }
    .pt-2         { padding-top: 4px; }
    .pt-3         { padding-top: 6px; }
    .space-y-1 > * + * { margin-top: 2px; }
    .space-y-2 > * + * { margin-top: 4px; }
    .border-t          { border-top: 1px dashed #000; }
    .border-b          { border-bottom: 1px dashed #000; }
    .flex            { display: flex; }
    .justify-between { justify-content: space-between; }
    .text-green-600 { color: #16a34a; }
    .text-red-600 { color: #dc2626; }
    .text-yellow-600 { color: #ca8a04; }
    .rounded-lg.border-2.border-dashed { border: none !important; padding: 0 !important; }
    .bg-slate-50 { background-color: transparent !important; }
  </style>
</head>
<body>
  ${copiesHTML}
</body>
</html>`)
    doc.close()

    iframe.onload = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe)
        }
      }, 1500)
    }
  }

  const exportToExcel = async () => {
    if (!sessionId) return
    try {
      toast.info('Generando archivo Excel...')
      const blob = await cashHistoryService.exportSession(sessionId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Corte_Caja_${sessionId}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Excel exportado correctamente')
    } catch (err: any) {
      toast.error('Error al exportar el archivo Excel')
    }
  }

  useEffect(() => {
    if (!open || sessionId === null) return
    let active = true
    setLoading(true)
    setError(false)

    cashHistoryService.getSession(sessionId, page, 10)
      .then((response) => {
        if (active) setDetail(response)
      })
      .catch(() => {
        if (active) setError(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => { active = false }
  }, [open, page, refreshKey, sessionId])

  useEffect(() => {
    if (open) {
      setPage(1)
      setDetail(null)
      setTicketCopies(1)
    }
  }, [open, sessionId])

  useEffect(() => {
    if (open && sessionId && detail && autoPrint && !loading) {
      const timer = setTimeout(() => {
        printTicket()
        onPrintComplete?.()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [open, sessionId, detail, autoPrint, loading])

  const session = detail?.session
  const status = session ? detailStatus[session.status] : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl">
        <DialogHeader className="border-b border-border px-5 py-4 pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div className="flex flex-wrap items-center gap-3">
              <DialogTitle className="text-card-foreground">Detalle de sesión {session ? `#${session.id}` : ''}</DialogTitle>
              {status && <span className={`rounded-md border px-2 py-1 text-xs font-medium ${status.className}`}>{status.label}</span>}
            </div>
            {session && detail && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={ticketCopies || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value)
                    setTicketCopies(isNaN(val) ? 0 : val)
                  }}
                  onBlur={() => {
                    if (!ticketCopies || ticketCopies < 1) setTicketCopies(1)
                  }}
                  className="h-9 w-16 text-center"
                  title="Copias a imprimir"
                />
                <Button variant="outline" size="sm" onClick={exportToExcel}>
                  <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                  Excel
                </Button>
                <Button variant="default" size="sm" onClick={printTicket}>
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimir
                </Button>
              </div>
            )}
          </div>
          <DialogDescription className="text-table-foreground/70">Información del corte y movimientos registrados en la caja.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {loading && !detail ? (
            <div className="flex min-h-72 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /><span className="ml-2 text-sm text-slate-500">Cargando sesión...</span></div>
          ) : error ? (
            <div className="flex min-h-72 flex-col items-center justify-center text-center">
              <RefreshCw className="h-8 w-8 text-slate-400" />
              <p className="mt-3 text-sm text-slate-600">No fue posible cargar el detalle.</p>
              <Button variant="outline" className="mt-4" onClick={() => setRefreshKey((value) => value + 1)}><RefreshCw />Reintentar</Button>
            </div>
          ) : session && detail ? (
            <div className="space-y-6">
              <section className="grid gap-x-6 gap-y-4 border-b border-slate-200 pb-5 sm:grid-cols-2 lg:grid-cols-4">
                <div><p className="text-xs uppercase text-table-foreground/70">Sucursal</p><p className="mt-1 text-sm font-medium text-table-foreground">{session.sucursal?.name ?? '—'}</p></div>
                <div><p className="text-xs uppercase text-table-foreground/70">Caja</p><p className="mt-1 text-sm font-medium text-table-foreground">{session.cash_register?.name ?? '—'} · {session.cash_register?.code ?? '—'}</p></div>
                <div><p className="text-xs uppercase text-table-foreground/70">Abrió</p><p className="mt-1 text-sm font-medium text-table-foreground">{person(session.opened_by)}</p><p className="text-xs text-slate-500">{dateTime(session.opened_at)}</p></div>
                <div><p className="text-xs uppercase text-table-foreground/70">Cerró / canceló</p><p className="mt-1 text-sm font-medium text-table-foreground">{person(session.closed_by ?? session.cancelled_by)}</p><p className="text-xs text-slate-500">{dateTime(session.closed_at ?? session.cancelled_at)}</p></div>
              </section>

              <section>
                <h3 className="mb-3 text-sm font-semibold text-table-foreground">Importes de la sesión</h3>
                <div className="grid overflow-hidden rounded-md border-border sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    ['Fondo inicial', session.opening_cash_amount],
                    ['Efectivo esperado', session.expected_cash_amount],
                    ['Efectivo contado', session.counted_cash_amount],
                    ['Diferencia', session.difference_amount],
                    ['Ventas en efectivo', session.cash_sales_amount],
                    ['Tarjeta de débito', session.card_sales_amount],
                    ['Tarjeta de crédito', session.credit_card_sales_amount],
                    ['Transferencias', session.transfer_sales_amount],
                    ['Saldo físico', session.cash_balance_amount],
                    ['Devoluciones', session.refund_amount],
                    ['Retiros', session.withdraw_amount],
                  ].map(([label, value]) => (
                    <div key={label} className={`px-3 py-3 border border-slate-200 sm:border-l`}>
                      <p className="text-xs text-table-foreground/70">{label}</p>
                      <p className={`mt-1 text-sm font-semibold ${label === 'Diferencia' && Number(value ?? 0) < 0 ? 'text-red-700' : 'text-table-foreground'}`}>{money(value)}</p>
                    </div>
                  ))}
                </div>
                {session.notes && <div className="mt-3 border-l-2 border-border pl-3"><p className="text-xs uppercase text-table-foreground/70">Notas</p><p className="mt-1 whitespace-pre-wrap text-sm text-table-foreground">{session.notes}</p></div>}
              </section>

              

             <div className="grid gap-5 lg:grid-cols-2">
  <DenominationTable
    title="Denominaciones de apertura"
    rows={session.opening_denominations ?? []}
  />

  <DenominationTable
    title="Denominaciones de cierre"
    rows={session.closure_denominations ?? []}
  />
</div>

<section>
  <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
    <div>
      <h3 className="text-sm font-semibold text-table-foreground">
        Movimientos de la sesión
      </h3>

      <p className="text-xs text-table-foreground/70">
        {detail.movement_summary.movements_count} movimientos registrados
      </p>
    </div>

    <div className="flex gap-4 text-xs">
      <span className="text-emerald-400">
        Entradas:{' '}
        <strong>{money(detail.movement_summary.incoming_total)}</strong>
      </span>

      <span className="text-red-400">
        Salidas:{' '}
        <strong>{money(detail.movement_summary.outgoing_total)}</strong>
      </span>

      <span className="text-table-foreground">
        Neto:{' '}
        <strong>{money(detail.movement_summary.net_total)}</strong>
      </span>
    </div>
  </div>

  <div className="overflow-hidden rounded-md border border-border bg-table-bg text-table-foreground">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] whitespace-nowrap text-sm">
        <thead className="bg-table-header-bg text-xs uppercase text-table-header-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Fecha</th>
            <th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-left">Concepto</th>
            <th className="px-3 py-2 text-left">Usuario</th>
            <th className="px-3 py-2 text-right">Importe</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
          {detail.movements.data.map((movement: CashMovementHistory) => {
            const incoming = movement.direction === 'IN'

            return (
              <tr
                key={movement.id}
                className="transition-colors hover:bg-table-row-hover"
              >
                <td className="px-3 py-2 text-table-foreground">
                  {dateTime(movement.occurred_at)}
                </td>

                <td className="px-3 py-2 text-table-foreground">
                  {movementNames[movement.movement_type] ??
                    movement.movement_type}
                </td>

                <td
                  className="max-w-72 truncate px-3 py-2 text-table-foreground"
                  title={movement.concept}
                >
                  {movement.concept}
                </td>

                <td className="px-3 py-2 text-table-foreground">
                  {person(movement.user)}
                </td>

                <td
                  className={`px-3 py-2 text-right font-semibold ${
                    incoming ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {incoming ? (
                      <ArrowDownLeft className="h-3 w-3" />
                    ) : (
                      <ArrowUpRight className="h-3 w-3" />
                    )}

                    {incoming ? '+' : '−'} {money(movement.amount)}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>

    <GlobalPaginator
      totalItems={detail.movements.total}
      itemsPerPage={detail.movements.per_page}
      currentPage={detail.movements.current_page}
      from={detail.movements.from}
      to={detail.movements.to}
      onPageChange={setPage}
    />
  </div>
</section>
            </div>
          ) : null}

          {/* Hidden Ticket for Printing */}
          <div className="hidden">
            {session && detail && (() => {
              const differenceAmount = parseFloat(session.difference_amount || '0')
              const status = differenceAmount === 0 ? 'success' : differenceAmount > 0 ? 'surplus' : 'shortage'
              const formatMoney = (val: string | number | null | undefined) => '$' + parseFloat(String(val || '0')).toFixed(2)

              return (
                <div ref={ticketRef} className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 font-mono text-sm">
                  <div className="mb-4 text-center">
                    <h3 className="text-base font-bold">Farmacias Salud Plus</h3>
                    <p className="text-xs">TICKET DE CORTE DE CAJA</p>
                  </div>
                  <div className="mb-3 border-t border-dashed border-slate-300 pt-2">
                    <p className="text-xs">Folio: {session.closure_folio}</p>
                    <p className="text-xs">Sucursal: {session.sucursal?.name || '—'}</p>
                    <p className="text-xs">Caja: {session.cash_register?.name || '—'}</p>
                    <p className="text-xs">Cajero: {[session.opened_by?.name, session.opened_by?.last_name].filter(Boolean).join(' ')}</p>
                    <p className="text-xs">Fecha: {session.closed_at ? new Date(session.closed_at).toLocaleString() : new Date().toLocaleString()}</p>
                  </div>
                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <h4 className="mb-2 text-xs font-bold text-slate-900">RESUMEN DE SESIÓN</h4>
                    <div className="flex justify-between text-xs"><span>Fondo inicial:</span><span>{formatMoney(session.opening_cash_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Efectivo):</span><span>{formatMoney(session.cash_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Débito):</span><span>{formatMoney(session.card_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Crédito):</span><span>{formatMoney(session.credit_card_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Transf):</span><span>{formatMoney(session.transfer_sales_amount)}</span></div>
                  </div>
                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <h4 className="mb-2 text-xs font-bold text-slate-900">MOVIMIENTOS DE CAJA</h4>
                    {detail.movements.data.length > 0 ? (
                      <div className="space-y-1 mb-2">
                        {detail.movements.data.map((m: any) => (
                          <div key={m.id} className="flex justify-between text-xs">
                            <span className="truncate pr-2">{m.concept || movementNames[m.movement_type] || 'Movimiento'}</span>
                            <span>
                              {m.direction === 'IN' ? '+' : '−'}{formatMoney(m.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 italic mb-2">Sin movimientos registrados</p>
                    )}
                    <div className="flex justify-between text-xs border-t border-slate-200 pt-1"><span>Total ingresos:</span><span className="font-semibold">{formatMoney(detail.movement_summary.incoming_total)}</span></div>
                    <div className="flex justify-between text-xs"><span>Total egresos:</span><span className="font-semibold">{formatMoney(detail.movement_summary.outgoing_total)}</span></div>
                  </div>
                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <div className="flex justify-between text-sm"><span>Efectivo esperado:</span><span className="font-semibold">{formatMoney(session.expected_cash_amount)}</span></div>
                    <div className="flex justify-between text-sm"><span>Efectivo contado:</span><span className="font-semibold">{formatMoney(session.counted_cash_amount)}</span></div>
                  </div>
                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <h4 className="mb-2 text-xs font-bold text-slate-900">DESGLOSE DE EFECTIVO</h4>
                    <div className="space-y-1 rounded bg-slate-50 p-2 pl-2 text-xs">
                      {session.closure_denominations && session.closure_denominations.length > 0 ? (
                        [...session.closure_denominations]
                          .sort((a, b) => parseFloat(b.denomination) - parseFloat(a.denomination))
                          .map((denom) => (
                            <div key={denom.denomination} className="flex justify-between">
                              <span>${denom.denomination} x {denom.quantity}:</span>
                              <span>${Number(denom.total_amount).toLocaleString()}</span>
                            </div>
                          ))
                      ) : (
                        <p className="text-xs text-slate-500 italic">Sin desglose de denominaciones</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 border-t border-dashed border-slate-300 pt-3">
                    <div className="flex justify-between text-base font-bold">
                      <span>{status === 'success' ? 'CORTE EXITOSO' : status === 'surplus' ? 'SOBRANTE:' : 'FALTANTE:'}</span>
                      <span>{status === 'success' ? '$0.00' : formatMoney(Math.abs(differenceAmount))}</span>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-center text-xs">
                    <p>Firma del cajero</p><div className="mb-2 border-b border-slate-400" style={{ marginTop: '40px' }}></div>
                    <p className="mt-4">Firma del supervisor</p><div className="mb-2 border-b border-slate-400" style={{ marginTop: '40px' }}></div>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
